import React from 'react';
import { StayWire } from '../../types/site';
import * as THREE from 'three';

interface StayWiresProps {
  stayWires: StayWire[];
  hoveredSet?: { height: number; distance: number; length: number; count: number } | null;
}

const StayWires: React.FC<StayWiresProps> = ({ stayWires, hoveredSet }) => {
  const TOWER_RADIUS = 0.3; // Must match TowerModel.tsx

  return (
    <group>
      {stayWires.map((wire) => {
        // Calculate tower attachment point
        const angle = (wire.angle * Math.PI) / 180;
        const towerX = Math.cos(angle) * TOWER_RADIUS;
        const towerZ = Math.sin(angle) * TOWER_RADIUS;
        const towerY = wire.height;

        // Calculate ground anchor point
        const groundX = Math.cos(angle) * wire.distance;
        const groundZ = Math.sin(angle) * wire.distance;
        const groundY = 0;

        // Calculate line geometry
        const start = new THREE.Vector3(towerX, towerY, towerZ);
        const end = new THREE.Vector3(groundX, groundY, groundZ);
        const length = start.distanceTo(end);
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

        // Check if this wire is part of the hovered set
        const isHovered = hoveredSet && 
                         wire.height === hoveredSet.height && 
                         wire.distance === hoveredSet.distance;

        // Create a cylinder to represent the wire
        const radius = isHovered ? 0.03 : 0.01; // Much thicker when hovered (3x)
        const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);

        // Rotate cylinder to align with line direction
        const quaternion = new THREE.Quaternion();
        const up = new THREE.Vector3(0, 1, 0);
        quaternion.setFromUnitVectors(up, direction);

        return (
          <mesh key={wire.id} position={center} quaternion={quaternion} geometry={geometry}>
            <meshStandardMaterial 
              color={isHovered ? "#60a5fa" : "#404040"} 
              metalness={0.8} 
              roughness={0.2}
              emissive={isHovered ? "#60a5fa" : "#000000"}
              emissiveIntensity={isHovered ? 0.5 : 0}
            />
          </mesh>
        );
      })}
    </group>
  );
};

export default React.memo(StayWires, (prevProps, nextProps) => {
  return prevProps.stayWires === nextProps.stayWires &&
         prevProps.hoveredSet?.height === nextProps.hoveredSet?.height &&
         prevProps.hoveredSet?.distance === nextProps.hoveredSet?.distance;
});

